import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
  ViewContainerRef,
  signal,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { ContinuationIndicatorComponent } from './continuation-indicator/continuation-indicator.component';
import * as SvgResources from '../../lib/svg.resources';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FacetedFilterPanelSearch } from '../../services/faceted-filter-panel-search.service';
import { FilterPaneManagerService } from '../../services/filter-pane-manager.service';

import { FilterPanelRef } from '../../types/filter-panel-ref';

import {
  IFacetedFilterDataItem,
  IFacetedFilterSearchResult,
  IFacetedFilterSelection,
  IReplayItem,
} from '../../types/faceted-filter.types';
import { ReplayAction } from '../../Utils/replay-action';

@Component({
  templateUrl: './faceted-filter-panel.component.html',
  styleUrls: ['./faceted-filter-panel.component.scss'],
  providers: [FilterPaneManagerService],
  standalone: true,
  imports: [AsyncPipe, NgClass, MatListModule, ContinuationIndicatorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacetedFilterPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly filterDataInjector = inject(Injector);
  private readonly paneManager = inject(FilterPaneManagerService);
  private readonly searchService = inject(FacetedFilterPanelSearch);
  private readonly filterPanelRef = inject(FilterPanelRef);

  private _destroy$ = new Subject<boolean>();

  filterDataItems: IFacetedFilterDataItem[] = [];
  selectedRootItem: IFacetedFilterDataItem | undefined;

  readonly signalIsSearching = signal(false);
  searchResults$!: Observable<IFacetedFilterSearchResult[]>;

  chevronIcon = SvgResources.sizableSvgIcon(SvgResources.svgIconTypes.chevronRight, 4);

  readonly paneComponentHost = viewChild.required('paneComponentHost', { read: ViewContainerRef });

  ngOnInit(): void {
    this.filterPanelRef.config.inputData.pipe(takeUntil(this._destroy$)).subscribe((data): void => {
      this.filterDataItems = [...(data.inputData as IFacetedFilterDataItem[])];
    });

    this.searchService.searchState$
      .pipe(takeUntil(this._destroy$))
      .subscribe((state: { isSearching: boolean }): void => {
        this.paneManager.purgeAllLoadedComponents();
        this.signalIsSearching.set(state.isSearching);
        if (!state.isSearching) this.selectedRootItem = undefined;
      });
    this.searchResults$ = this.searchService.searchResults$;
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.checkServiceHost();
    setTimeout((): void => {
      if (this.filterPanelRef.config.inputFacetData) {
        this.replaySelectionData(this.filterPanelRef.config.inputFacetData);
      }
    }, 1);
  }

  onRootItemSelected(item: IFacetedFilterDataItem): void {
    this.selectedRootItem = undefined;
    this.selectedRootItem = item;

    // this.checkServiceHost();

    this.paneManager.purgeAllLoadedComponents();
    this.paneManager.loadFilterPane(item);
  }

  onSearchResultItemSelected(result: IFacetedFilterSearchResult): void {
    this.searchService.clearSearch();

    // this.checkServiceHost();

    const pathItems: string[] = result.path.split('|');
    const parentItem = this.filterDataItems.find((f) => f.label === pathItems[0]);
    if (!parentItem) return;

    pathItems.shift();

    this.selectedRootItem = undefined;
    this.selectedRootItem = parentItem;

    const replayItems: IReplayItem[] = ReplayAction.GetItemsToReplay(pathItems, parentItem);

    this.paneManager.replayResult(replayItems);
  }

  replaySelectionData(selection: IFacetedFilterSelection): void {
    // this.checkServiceHost();
    const pathItems: string[] = selection.path.split('|');
    const parentItem = this.filterDataItems.find((f) => f.label === pathItems[0]);
    if (!parentItem) return;

    this.selectedRootItem = undefined;
    this.selectedRootItem = parentItem;

    pathItems.shift();

    const replayItems: IReplayItem[] = ReplayAction.GetItemsToReplay(pathItems, parentItem, selection.value);

    this.paneManager.replayResult(replayItems);
  }

  private checkServiceHost(): void {
    if (!this.paneManager.hasContainerRefHost()) this.paneManager.setContainerRefHost(this.paneComponentHost());
  }
}
